// @ts-nocheck
import Editor from '../../Editor/Editor';
import { useState, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Clock, Trash, MapPin, DotsSixVertical } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { imageSize } from '../../../constants/files';
import { getLineItemImageUrl } from '../../../utils/resourcePathHelper';
import { formatTimeLocale } from '../../../utils/dateUtility';
import { getLineItemServiceType } from '../../../constants/lineItemServiceTypes';
import { useTimeFormat } from '../../../hooks/useTimeFormat';
import DeleteModal from '../../Trip/DeleteModal/DeleteModal';
import * as PropTypes from 'prop-types';
import * as P from '@phosphor-icons/react';
import './LineItemCard.css';

const getServiceTypeIcon = (iconRef = 'Pin') => {
  const IconComponent = P[iconRef] || MapPin;
  return <IconComponent size={20} />;
};

/**
 * LineItemCard component displays a card for a line item (service) within a trip itinerary or library.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.lineItem - The line item data containing details about the service.
 * @param {function} props.onEdit - Callback function to be called when the line item is edited.
 * @param {function} props.onDelete - Callback function to be called when the line item is deleted.
 * @param {number} props.tripId - The ID of the trip (optional for library items).
 * @param {number} props.tenantId - The ID of the tenant.
 * @param {boolean} props.isLibraryItem - Whether this is a library item (tripdayid is null).
 * @param {boolean} props.isDraggable - Whether the card can be dragged.
 *
 * @returns {JSX.Element} A JSX element representing the line item card with appropriate details and actions.
 */
const LineItemCard = ({
  lineItem,
  onEdit,
  onDelete,
  // eslint-disable-next-line no-unused-vars
  tripId,
  // eslint-disable-next-line no-unused-vars
  tenantId,
  // eslint-disable-next-line no-unused-vars
  isLibraryItem = false,
  isDraggable = false,
  dragHandleProps,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common']);
  const { timeFormat } = useTimeFormat();

  // Helper function to check if a value has meaningful content (not just whitespace)
  const hasValue = (value) => {
    return value && typeof value === 'string' && value.trim() !== '';
  };

  // Get service type info from mapping (includes icon and color)
  const serviceTypeInfo = getLineItemServiceType(lineItem?.servicetypecode);
  const serviceType = serviceTypeInfo || {
    code: lineItem?.servicetypecode,
    name: lineItem?.servicetypename,
    iconref: 'MapPin',
    color: '#6b7280'
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const pointerStateRef = useRef({
    isDown: false,
    startX: 0,
    startY: 0,
    didDrag: false
  });

  // const getImageUrl = (resourcePathOrFileName = '') => {
  //   return getLineItemImageUrl({
  //     resourcePath: resourcePathOrFileName,
  //     sizePrefix: imageSize.thumbnail.prefix
  //   });
  // };

  // Map LineItem fields based on servicetypecode
  // See XG-DB/==Migrations/Migrations_20260120_Service_LineItem_Mapping.md
  const vendorName = hasValue(lineItem?.vendorname) ? lineItem.vendorname : '';
  const startTime = hasValue(lineItem?.starttime) ? lineItem.starttime : '';
  const title = hasValue(lineItem?.title) ? lineItem.title : '';
  const description = hasValue(lineItem?.description)
    ? lineItem.description
    : '';

  // Process images from lineitemimage array
  // Create a copy before sorting to avoid mutating Redux state
  const images = [...(lineItem?.lineitemimage || [])]
    .sort((a, b) => a.seq - b.seq)
    .map((img) =>
      getLineItemImageUrl({
        resourcePath: img.resourcepath,
        sizePrefix: imageSize.thumbnail.prefix
      })
    )
    .filter(Boolean);

  // Format time for display
  const formattedTime = formatTimeLocale(startTime, timeFormat);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(lineItem.lineitemid);
        setIsDeleteModalOpen(false);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [onDelete, lineItem.lineitemid]);

  const handlePointerDown = useCallback((e) => {
    pointerStateRef.current.isDown = true;
    pointerStateRef.current.didDrag = false;
    pointerStateRef.current.startX = e?.clientX ?? 0;
    pointerStateRef.current.startY = e?.clientY ?? 0;
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!pointerStateRef.current.isDown) return;

    const x = e?.clientX ?? 0;
    const y = e?.clientY ?? 0;
    const dx = Math.abs(x - pointerStateRef.current.startX);
    const dy = Math.abs(y - pointerStateRef.current.startY);

    if (dx > 5 || dy > 5) {
      pointerStateRef.current.didDrag = true;
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    pointerStateRef.current.isDown = false;
  }, []);

  const handleCardClick = useCallback(() => {
    if (pointerStateRef.current.didDrag) return;
    if (!isDeleteModalOpen && onEdit) {
      onEdit(lineItem);
    }
  }, [isDeleteModalOpen, onEdit, lineItem]);

  const dragHandleEventProps = {
    ...(dragHandleProps || {}),
    onMouseDown: (e) => {
      dragHandleProps?.onMouseDown?.(e);
      handlePointerDown(e);
    },
    onMouseMove: (e) => {
      dragHandleProps?.onMouseMove?.(e);
      handlePointerMove(e);
    },
    onMouseUp: (e) => {
      dragHandleProps?.onMouseUp?.(e);
      handlePointerUp();
    },
    onMouseLeave: (e) => {
      dragHandleProps?.onMouseLeave?.(e);
      handlePointerUp();
    },
    onTouchStart: (e) => {
      dragHandleProps?.onTouchStart?.(e);
      const t = e?.touches?.[0];
      handlePointerDown({ clientX: t?.clientX ?? 0, clientY: t?.clientY ?? 0 });
    },
    onTouchMove: (e) => {
      dragHandleProps?.onTouchMove?.(e);
      const t = e?.touches?.[0];
      handlePointerMove({ clientX: t?.clientX ?? 0, clientY: t?.clientY ?? 0 });
    },
    onTouchEnd: (e) => {
      dragHandleProps?.onTouchEnd?.(e);
      handlePointerUp();
    }
  };

  return (
    <Box
      className='line-item-card'
      onClick={handleCardClick}
      {...dragHandleEventProps}
      {...rest}
    >
      {isDraggable && (
        <Box className='line-item-card__drag-handle' data-drag-handle='true'>
          <DotsSixVertical size={20} />
        </Box>
      )}

      <Box className='line-item-card__image'>
        {images.length > 0 ? (
          <img
            src={images[0]}
            alt={title || ''}
            className='line-item-card__image-thumbnail'
          />
        ) : (
          <Box className='line-item-card__image-placeholder'>
            {getServiceTypeIcon(serviceType?.iconref)}
          </Box>
        )}
      </Box>

      <Box className='line-item-card__content'>
        <Box className='line-item-card__header'>
          <Box className='line-item-card__type'>
            <Box className='line-item-card__type-icon'>
              {getServiceTypeIcon(serviceType?.iconref)}
            </Box>
            <Typography variant='caption' className='line-item-card__type-text'>
              {serviceType?.name}
            </Typography>
          </Box>

          <Typography variant='subtitle2' className='line-item-card__title'>
            {title || ''}
          </Typography>
        </Box>

        <Box className='line-item-card__details'>
          {hasValue(startTime) && (
            <Box className='line-item-card__detail'>
              <Clock size={16} />
              <Typography variant='body2'>{formattedTime}</Typography>
            </Box>
          )}

          {hasValue(vendorName) && (
            <Box className='line-item-card__detail'>
              <MapPin size={16} />
              <Typography variant='body2'>{vendorName}</Typography>
            </Box>
          )}
        </Box>

        {hasValue(description) && (
          <Editor
            id={`line-item-editor-${lineItem.lineitemid}-description`}
            content={description}
            editable={false}
            isPreviewing={true}
            className='line-item-card__description'
          />
        )}
      </Box>

      {onDelete && (
        <Box
          className='line-item-card__actions'
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Tooltip title={t('common:delete')}>
            <IconButton
              size='small'
              onClick={handleDelete}
              className='line-item-card__delete-btn'
              disabled={isDeleting}
            >
              <Trash size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <DeleteModal
        id={`delete-line-item-modal-${lineItem.lineitemid}`}
        isOpenModal={isDeleteModalOpen}
        title={t('trip:deleteServiceDay')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        description={`${t('trip:serviceDayDeletionConfirmation')} ${t('common:thisActionCannotBeUndone')}`}
        isSubmitting={isDeleting}
      />
    </Box>
  );
};

LineItemCard.propTypes = {
  lineItem: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  tripId: PropTypes.number,
  tenantId: PropTypes.number,
  isLibraryItem: PropTypes.bool,
  isDraggable: PropTypes.bool,
  dragHandleProps: PropTypes.object
};

export default LineItemCard;
